// Cross-catalog navigation — links all 5 NYC product catalogs together to
// solve thin-hub isolation and boost internal PageRank flow between them.
// A5 unique copy — never duplicated across pages; each catalog gets its own
// contextual tagline. Rendered inside each catalog index page.
import { Link } from "@tanstack/react-router";
import { Award, Stamp, Scroll, Languages, Plane } from "lucide-react";

type Locale = "th" | "en";

const CATALOGS = [
  {
    id: "naati",
    thPath: "/naati/products",
    enPath: "/en/naati/products",
    icon: Award,
    accent: "from-blue-50 to-white border-blue-200 hover:border-blue-400",
    titleTh: "NAATI Certified Translation",
    titleEn: "NAATI Certified Translation",
    tagTh: "แปลรับรอง NAATI ยื่นวีซ่าออสเตรเลีย · 28 SKU · ฿1,300+",
    tagEn: "NAATI-endorsed translation for Australian visa lodgement · 28 SKUs · from THB 1,300",
  },
  {
    id: "notary",
    thPath: "/notary/products",
    enPath: "/en/notary/products",
    icon: Stamp,
    accent: "from-amber-50 to-white border-amber-200 hover:border-amber-400",
    titleTh: "Notarial Services Attorney",
    titleEn: "Notarial Services Attorney",
    tagTh: "รับรองเอกสาร Notary Public โดยทนายจดทะเบียนสภาทนายความ · 66 SKU",
    tagEn: "Notarisation by Thai Bar-registered Notarial Services Attorney · 66 SKUs",
  },
  {
    id: "apostille",
    thPath: "/apostille/products",
    enPath: "/en/apostille/products",
    icon: Scroll,
    accent: "from-emerald-50 to-white border-emerald-200 hover:border-emerald-400",
    titleTh: "Apostille & Legalisation",
    titleEn: "Apostille & Legalisation",
    tagTh: "รับ Apostille 130 ประเทศ Hague · เดินเอกสาร MFA/สถานทูตครบวงจร",
    tagEn: "Hague Apostille across 130 countries · full MFA & embassy legalisation",
  },
  {
    id: "sworn",
    thPath: "/sworn-translator/products",
    enPath: "/en/sworn-translator/products",
    icon: Languages,
    accent: "from-purple-50 to-white border-purple-200 hover:border-purple-400",
    titleTh: "Sworn Translation (EU)",
    titleEn: "Sworn Translation (EU)",
    tagTh: "นักแปลจดทะเบียน MoJ + เครือข่าย sworn ปลายทาง DE/FR/ES/IT/NL/PL",
    tagEn: "MoJ-registered translators + destination sworn network (DE/FR/ES/IT/NL/PL)",
  },
  {
    id: "visa",
    thPath: "/visa/products",
    enPath: "/en/visa/products",
    icon: Plane,
    accent: "from-rose-50 to-white border-rose-200 hover:border-rose-400",
    titleTh: "Thai Visa Application",
    titleEn: "Thai Visa Application",
    tagTh: "บริการยื่นวีซ่าไทยครบทุกประเภท · Non-B · LTR · DTV · Elite · Marriage",
    tagEn: "End-to-end Thai visa lodgement · Non-B · LTR · DTV · Elite · Marriage",
  },
];

export interface ProductCatalogHubNavProps {
  locale: Locale;
  current: "naati" | "notary" | "apostille" | "sworn" | "visa";
}

export function ProductCatalogHubNav({ locale, current }: ProductCatalogHubNavProps) {
  const others = CATALOGS.filter((c) => c.id !== current);
  const headingTh = "สำรวจแคตตาล็อกอื่นของ NYC Language Institute";
  const headingEn = "Explore other NYC Language Institute catalogues";
  const subTh = "แต่ละแคตตาล็อกจัดหมวดตามใบอนุญาตวิชาชีพ ราคาเปิดเผยชัดเจน และมี JSON-LD Product schema พร้อม Google Merchant feed";
  const subEn = "Each catalogue is scoped by professional licence, transparently priced, and ships JSON-LD Product schema plus a Google Merchant feed";

  return (
    <section className="mx-auto mt-16 max-w-6xl px-4" aria-labelledby="catalog-hub-nav">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8">
        <h2 id="catalog-hub-nav" className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          {locale === "th" ? headingTh : headingEn}
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-slate-600 sm:text-base">
          {locale === "th" ? subTh : subEn}
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {others.map((c) => {
            const Icon = c.icon;
            const to = locale === "th" ? c.thPath : c.enPath;
            const title = locale === "th" ? c.titleTh : c.titleEn;
            const tag = locale === "th" ? c.tagTh : c.tagEn;
            return (
              <Link
                key={c.id}
                to={to}
                className={`group rounded-2xl border bg-gradient-to-b ${c.accent} p-5 transition hover:-translate-y-0.5 hover:shadow-md`}
              >
                <Icon className="mb-2 h-6 w-6 text-navy-deep" aria-hidden />
                <div className="font-display text-base font-extrabold text-navy-deep">{title}</div>
                <div className="mt-1 text-xs leading-relaxed text-slate-600">{tag}</div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
